import * as React from 'react';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface GlassTimePickerProps {
  value: string; // 'HH:mm' ou ''
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

function parseTime(v: string): { h: string; m: string } {
  if (!v) return { h: '', m: '' };
  const parts = v.split(':');
  return {
    h: parts[0] ?? '',
    m: parts[1] ? parts[1].slice(0, 2) : '',
  };
}

export function GlassTimePicker({
  value,
  onChange,
  placeholder = 'Selecione a hora',
  disabled = false,
  className,
}: GlassTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const hourRef = React.useRef<HTMLDivElement | null>(null);
  const minRef = React.useRef<HTMLDivElement | null>(null);

  const { h: selectedH, m: selectedM } = parseTime(value);

  // Scroll para o item selecionado quando popup abre
  React.useEffect(() => {
    if (!open) return;
    const scrollTo = (ref: React.RefObject<HTMLDivElement | null>, selected: string) => {
      if (!ref.current || !selected) return;
      const btn = ref.current.querySelector(`[data-val="${selected}"]`) as HTMLElement | null;
      if (btn) btn.scrollIntoView({ block: 'center' });
    };
    // Aguarda o DOM renderizar
    const t = setTimeout(() => {
      scrollTo(hourRef, selectedH);
      scrollTo(minRef, selectedM);
    }, 0);
    return () => clearTimeout(t);
  }, [open, selectedH, selectedM]);

  // Fecha ao clicar fora
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function selectHour(h: string) {
    const m = selectedM || '00';
    onChange(`${h}:${m}`);
  }

  function selectMinute(m: string) {
    const h = selectedH || '00';
    onChange(`${h}:${m}`);
    setOpen(false);
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange('');
    setOpen(false);
  }

  const displayValue = value ? value.slice(0, 5) : '';

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-xl border bg-[var(--surface)] px-2.5 text-sm transition',
          'border-[var(--border)] text-[var(--fg)]',
          'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          open && 'ring-2 ring-[var(--accent)]',
        )}
      >
        <Clock className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" />
        <span className={cn('flex-1 text-left', !displayValue && 'text-[var(--fg-muted)]')}>
          {displayValue || placeholder}
        </span>
      </button>

      {open && (
        <div
          className={cn(
            'absolute left-0 z-50 mt-1 overflow-hidden rounded-xl border shadow-lg',
            'border-[var(--popover-border,var(--card-border))]',
            'backdrop-blur-[var(--blur,0px)]',
          )}
          style={{
            background: 'var(--popover-bg, var(--card-bg))',
            boxShadow: 'var(--shadow-card)',
            width: '140px',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-center gap-1 border-b border-[var(--popover-border,var(--card-border))] px-3 py-1.5">
            <span className="text-xs font-semibold text-[var(--fg)]">
              {selectedH || '--'}:{selectedM || '--'}
            </span>
          </div>

          {/* Colunas Horas / Minutos */}
          <div className="flex divide-x divide-[var(--popover-border,var(--card-border))]">
            {/* Horas */}
            <div
              ref={hourRef}
              className="flex-1 overflow-y-auto"
              style={{ maxHeight: '168px' }}
            >
              {HOURS.map((h) => (
                <button
                  key={h}
                  type="button"
                  data-val={h}
                  onClick={() => selectHour(h)}
                  className={cn(
                    'w-full py-1.5 text-xs transition',
                    h === selectedH
                      ? 'bg-[var(--accent)] text-white font-medium'
                      : 'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]',
                  )}
                >
                  {h}
                </button>
              ))}
            </div>

            {/* Minutos */}
            <div
              ref={minRef}
              className="flex-1 overflow-y-auto"
              style={{ maxHeight: '168px' }}
            >
              {MINUTES.map((m) => (
                <button
                  key={m}
                  type="button"
                  data-val={m}
                  onClick={() => selectMinute(m)}
                  className={cn(
                    'w-full py-1.5 text-xs transition',
                    m === selectedM
                      ? 'bg-[var(--accent)] text-white font-medium'
                      : 'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]',
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-start border-t border-[var(--popover-border,var(--card-border))] px-3 py-1.5">
            <button
              type="button"
              onClick={clear}
              className="text-[11px] text-[var(--fg-muted)] hover:text-[var(--fg)] transition"
            >
              Limpar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
